import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import {
  BF_AUTH_CONFIG,
  BF_AUTH_CONFIG_OPTIONS,
  type BfAuthConfig,
} from '@brafton/skynet-angular-security-service';

import { environment } from '../../environments/environment';

/**
 * The app's name in Auth0. Load-bearing in two places inside
 * `@brafton/skynet-angular-security-service`:
 *  - permissions are read from `app_metadata.apps['feedback-app']`
 *  - it is the `clientID` passed to `lock.logout()`
 */
export const SKYNET_APP_NAME = 'feedback-app';

/** Route the guard sends unauthenticated visitors to. */
export const LOGIN_ROUTE = '/login';

/** Route skynet lands on after a login that carries no return URL. */
export const HOME_ROUTE = '/projects';

/**
 * Auth0 database/enterprise connections offered by the Lock widget. Narrowing
 * this to the Brafton staff connections skips the connection picker. If sign-in
 * ever rejects a valid account, this is the first thing to check — dropping the
 * property entirely makes Lock offer every connection enabled for the client.
 */
const ALLOWED_CONNECTIONS = ['Staff-Users', 'brafton-com'];

/**
 * Builds the `BfAuthConfig` skynet reads.
 *
 * Deliberately declares **no `DelegateClients`**. On the non-legacy path skynet
 * stores the Auth0 *access token* as `id_token_delegate_<AppName>`, and without
 * an `audience` Auth0 issues an opaque access token — not a JWT. Skynet then
 * calls `BfAuthJwtHelper.isTokenExpired()` on that stored value on the next page
 * load, which throws `'JWT must have 3 parts'`. With no delegate clients nothing
 * is stored and that path is never taken. The Feedback API bearer token keeps
 * coming from `GET /v1/token` via the {@link authGuard}.
 *
 * `AutoLogin` is required by the `BfAuthConfig` interface but is never read by
 * the library (nor is `Auth0.AudienceName`) — set only to satisfy the type.
 */
function buildAuthConfig(): BfAuthConfig {
  return {
    Auth0: {
      Domain: environment.auth0.domain,
      ClientId: environment.auth0.clientId,
      AppName: SKYNET_APP_NAME,
    },
    LoginPageURL: LOGIN_ROUTE,
    HomePageURL: HOME_ROUTE,
    // Appended to window.location.origin as Auth0's `returnTo` on logout, so it
    // must be registered as an Allowed Logout URL for the client.
    LogoutURL: LOGIN_ROUTE,
    AutoLogin: false,
  };
}

/**
 * Builds the Auth0 Lock constructor options.
 *
 * This is a **redirect** flow, not a popup: Lock sends the browser to Auth0 and
 * Auth0 returns to `redirectUrl` with the tokens in the URL fragment, where
 * skynet's `handleAuthentication()` listener picks them up.
 *
 * The return URL is carried in Auth0's `state` as `ru=<path>`, which skynet
 * decodes and navigates to after storing the tokens. It is captured here, at
 * injection time, from the URL the user actually landed on. Per the library's
 * changelog `state` must be left empty when the path is `/`, otherwise skynet
 * would try to navigate back to `/` and loop.
 *
 * No `audience` is requested — see {@link buildAuthConfig} for why that rules
 * out delegate clients.
 */
function buildAuthConfigOptions(): Auth0LockConstructorOptions {
  const options: Auth0LockConstructorOptions = {
    // The login page renders only a backdrop — no card and no retry button — so Lock
    // must not be dismissable, or dismissing it would strand the visitor on the photo
    // with no way to sign in. atlas-app-v2 sets this the same way.
    closable: false,
    autoclose: true,
    allowedConnections: ALLOWED_CONNECTIONS,
    languageDictionary: { title: 'Feedback' },
    theme: {
      // The Brafton "b" mark shown in the Lock header, served from this app's own
      // assets (projects/feedback-app/public/brafton-mark.svg) rather than
      // hotlinked from brafton.com, so sign-in does not depend on that site.
      // Lock renders it as a plain <img src>, so an absolute URL is required.
      logo: `${globalThis.location?.origin ?? ''}/brafton-mark.svg`,
      // Matches --mat-sys-primary in styles.scss.
      primaryColor: '#207f86',
    },
    auth: {
      redirectUrl: globalThis.location?.origin,
      responseType: 'token id_token',
      sso: true,
      params: {
        // The standard OIDC scopes, NOT the per-claim list in the library's
        // README ('openid given_name family_name email picture app_metadata').
        // Those are pre-OIDC-conformant Auth0 style: on a modern client they do
        // not grant given_name/family_name/picture, so the profile and avatar
        // come back empty — and Lock's `sso: true` path (getSSOData) explicitly
        // requires `openid profile email`, warning in the console otherwise.
        //
        // `app_metadata` is deliberately absent: permissions arrive as the
        // namespaced 'https://brafton.com/app_metadata' claim injected by an
        // Auth0 rule, which the library reads directly and which needs no scope.
        scope: 'openid profile email',
      },
    },
  };

  const currentPath = globalThis.location?.pathname ?? '/';
  if (currentPath.length > 1) {
    const queryString = globalThis.location?.search?.replace(/^\?/, '') ?? '';
    const returnUrl = queryString ? `${currentPath}?${queryString}` : currentPath;
    options.auth!.params!.state = encodeURIComponent(`ru=${returnUrl}`);
  }

  return options;
}

/**
 * Registers the org auth library's configuration. Pair it with a
 * `handleAuthentication()` call in the root component — skynet only listens for
 * Auth0's callback once that runs.
 */
export function provideSkynetAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: BF_AUTH_CONFIG, useFactory: buildAuthConfig },
    { provide: BF_AUTH_CONFIG_OPTIONS, useFactory: buildAuthConfigOptions },
  ]);
}
